import { ClientMessage } from '../types/network';

/**
 * MessageQueue - Handles packet queuing and redispatch for unreliable messages.
 *
 * In L1J protocol, certain packets may need to be:
 * 1. Queued during connection interruptions
 * 2. Redispatched after reconnection
 * 3. Batch-processed to reduce network traffic
 */
export class MessageQueue {
  private pendingQueue: QueuedMessage[] = [];
  private dispatchInterval: number = 100; // ms between dispatches
  private maxQueueSize: number = 100;
  private dispatchTimer: number | null = null;
  private isProcessing: boolean = false;

  /**
   * @param onSend 실제 소켓 전송 함수 (ConnectionManager.sendRaw).
   *   미연결 시 throw해야 하며, 큐잉하는 send를 넘기면 재-enqueue 순환이
   *   생기므로 절대 연결하지 말 것.
   */
  constructor(private onSend: (msg: ClientMessage) => void) {}

  /**
   * Add a message to the queue for later dispatch.
   */
  enqueue(message: ClientMessage, priority: boolean = false): void {
    if (this.pendingQueue.length >= this.maxQueueSize) {
      // Drop oldest non-priority message if queue is full
      const dropIdx = this.pendingQueue.findIndex((m) => !m.priority);
      if (dropIdx >= 0) {
        this.pendingQueue.splice(dropIdx, 1);
      } else {
        console.warn('MessageQueue: Queue full, dropping message');
        return;
      }
    }

    const queued: QueuedMessage = {
      message,
      timestamp: Date.now(),
      priority,
      attempts: 0,
    };

    if (priority) {
      // Priority messages go to front
      this.pendingQueue.unshift(queued);
    } else {
      this.pendingQueue.push(queued);
    }
  }

  /**
   * Start automatic dispatch processing.
   */
  start(): void {
    if (this.dispatchTimer !== null) return;

    this.dispatchTimer = window.setInterval(() => {
      this.processQueue();
    }, this.dispatchInterval);
  }

  /**
   * Stop automatic dispatch processing.
   */
  stop(): void {
    if (this.dispatchTimer !== null) {
      window.clearInterval(this.dispatchTimer);
      this.dispatchTimer = null;
    }
  }

  /**
   * Process queued messages (called automatically by start()).
   */
  private processQueue(): void {
    if (this.isProcessing || this.pendingQueue.length === 0) return;

    this.isProcessing = true;

    try {
      // Get next message to dispatch
      const queued = this.pendingQueue[0];

      // Check if message should be dropped after max attempts
      if (queued.attempts >= 3) {
        this.pendingQueue.shift();
        console.warn('MessageQueue: Dropping message after 3 attempts', queued.message.type);
        return;
      }

      // Age-based priority: older messages get dispatched first
      const age = Date.now() - queued.timestamp;
      if (age < this.dispatchInterval && !queued.priority) {
        // Not yet time to dispatch this message
        return;
      }

      // Attempt to send
      try {
        this.onSend(queued.message);
        this.pendingQueue.shift();
      } catch (err) {
        queued.attempts++;
        console.warn('MessageQueue: Send failed, will retry', queued.attempts);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Clear all queued messages (e.g., after reconnection).
   */
  clear(): void {
    this.pendingQueue = [];
  }

  /**
   * Get current queue size.
   */
  size(): number {
    return this.pendingQueue.length;
  }

  /**
   * Check if queue is empty.
   */
  isEmpty(): boolean {
    return this.pendingQueue.length === 0;
  }

  /**
   * Dispatch all queued messages immediately (e.g., after reconnection).
   *
   * 전송(onSend = sendRaw)이 throw하면 해당 메시지를 큐 앞에 그대로 남겨두고
   * 루프를 중단한다 — 연결이 다시 끊긴 상황에서 나머지를 유실하거나
   * 무한 재시도하지 않기 위함. 다음 연결 성공 시 다시 flush된다.
   */
  flush(): void {
    while (this.pendingQueue.length > 0) {
      const queued = this.pendingQueue[0];
      try {
        this.onSend(queued.message);
        // 전송 성공 시에만 큐에서 제거
        this.pendingQueue.shift();
      } catch (err) {
        console.warn(
          'MessageQueue: Flush interrupted (not connected), will retry on next connection',
          err
        );
        break;
      }
    }
  }
}

interface QueuedMessage {
  message: ClientMessage;
  timestamp: number;
  priority: boolean;
  attempts: number;
}
